import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CornerDownRight,
  ExternalLink,
  IndentDecrease,
  IndentIncrease,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { Menu } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useReplaceMenuItems } from '../hooks/use-menu-mutations';
import {
  appendNode,
  canIndent,
  countNodes,
  emptyNode,
  fromMenuItems,
  indentNode,
  MAX_DEPTH,
  MAX_ITEMS,
  moveNode,
  nodeKeyForField,
  outdentNode,
  removeNode,
  toMenuItemsInput,
  updateNode,
  validateMenuTree,
  type MenuNode,
  type NodePath,
} from '../schemas/menu-tree';

import { MenuItemDialog, type MenuItemPatch } from './menu-item-dialog';

type Editing =
  | { mode: 'closed' }
  | { mode: 'edit'; path: NodePath; node: MenuNode }
  | { mode: 'add'; parentPath: NodePath; node: MenuNode };

interface NodeRowProps {
  node: MenuNode;
  path: NodePath;
  siblings: number;
  nodes: readonly MenuNode[];
  canWrite: boolean;
  errors: ReadonlyMap<string, string>;
  onEdit: (path: NodePath, node: MenuNode) => void;
  onAddChild: (path: NodePath) => void;
  onChange: (next: MenuNode[]) => void;
}

function NodeRow({
  node,
  path,
  siblings,
  nodes,
  canWrite,
  errors,
  onEdit,
  onAddChild,
  onChange,
}: NodeRowProps) {
  const { t } = useTranslation('menus');
  const depth = path.length;
  const index = path[path.length - 1] ?? 0;
  const error = errors.get(node.key);
  const label = node.title || t('tree.untitled');
  const destination =
    node.linkType === 'url'
      ? node.url
      : (node.url ?? (node.targetMissing ? null : t('tree.picked')));

  return (
    <li className="flex flex-col gap-1.5">
      <div
        data-testid="menu-node"
        aria-label={label}
        role="group"
        className={cn(
          'bg-card flex flex-wrap items-center gap-2 rounded-md border px-3 py-2',
          error && 'border-destructive',
        )}
      >
        {depth > 1 ? (
          <CornerDownRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {label}
            <Badge variant="outline" className="font-normal">
              {t(`linkType.${node.linkType}`)}
            </Badge>
            {node.targetMissing ? (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle aria-hidden />
                {t('tree.targetMissing')}
              </Badge>
            ) : null}
            {node.openInNewTab ? (
              <ExternalLink
                className="text-muted-foreground size-3.5"
                aria-label={t('tree.newTab')}
                role="img"
              />
            ) : null}
          </span>
          {destination ? (
            <span className="text-muted-foreground truncate font-mono text-xs">{destination}</span>
          ) : null}
          {error ? (
            <span role="alert" className="text-destructive text-xs">
              {error}
            </span>
          ) : null}
        </div>
        {canWrite ? (
          <div
            className="flex flex-wrap items-center gap-0.5"
            data-tour={depth === 1 && index === 0 ? 'content.menu-editor.node-actions' : undefined}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={t('tree.edit', { title: label })}
              onClick={() => onEdit(path, node)}
            >
              <Pencil aria-hidden />
            </Button>
            {depth < MAX_DEPTH ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={t('tree.addChild', { title: label })}
                onClick={() => onAddChild(path)}
              >
                <Plus aria-hidden />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={index === 0}
              aria-label={t('tree.moveUp', { title: label })}
              onClick={() => onChange(moveNode(nodes, path, -1))}
            >
              <ArrowUp aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={index === siblings - 1}
              aria-label={t('tree.moveDown', { title: label })}
              onClick={() => onChange(moveNode(nodes, path, 1))}
            >
              <ArrowDown aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={!canIndent(nodes, path)}
              aria-label={t('tree.indent', { title: label })}
              onClick={() => onChange(indentNode(nodes, path))}
            >
              <IndentIncrease aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={depth === 1}
              aria-label={t('tree.outdent', { title: label })}
              onClick={() => onChange(outdentNode(nodes, path))}
            >
              <IndentDecrease aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive size-8"
              aria-label={t('tree.remove', { title: label })}
              onClick={() => onChange(removeNode(nodes, path))}
            >
              <Trash2 aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
      {node.children.length > 0 ? (
        <ol className="ml-6 flex flex-col gap-1.5 border-l pl-3">
          {node.children.map((child, childIndex) => (
            <NodeRow
              key={child.key}
              node={child}
              path={[...path, childIndex]}
              siblings={node.children.length}
              nodes={nodes}
              canWrite={canWrite}
              errors={errors}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onChange={onChange}
            />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/**
 * The menu's items as a tree, three levels deep, edited locally and saved as a whole with PUT.
 * Client checks mirror the backend's; the backend's own refusals land on the item they name.
 */
export function MenuTreeEditor({ storeId, menu }: { storeId: string; menu: Menu }) {
  const { t } = useTranslation('menus');
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const replace = useReplaceMenuItems(storeId);
  const initial = useMemo(() => fromMenuItems(menu.items), [menu.items]);
  const [nodes, setNodes] = useState<MenuNode[]>(initial);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [rootError, setRootError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>({ mode: 'closed' });
  const total = countNodes(nodes);

  const change = (next: MenuNode[]) => {
    setNodes(next);
    setDirty(true);
    setErrors(new Map());
    setRootError(null);
  };

  const save = async () => {
    const problems = validateMenuTree(nodes);
    if (problems.size > 0) {
      setErrors(
        new Map([...problems].map(([key, problem]) => [key, t(`validation.item.${problem}`)])),
      );
      setRootError(t('validation.fixItems'));
      return;
    }
    try {
      await replace.mutateAsync({ id: menu.id, body: { items: toMenuItemsInput(nodes) } });
      setDirty(false);
    } catch (error) {
      const next = new Map<string, string>();
      if (error instanceof ApiError) {
        for (const detail of error.details) {
          const key = nodeKeyForField(nodes, detail.field);
          if (key) next.set(key, detail.message);
        }
      }
      setErrors(next);
      setRootError(message(error));
    }
  };

  const discard = () => {
    setNodes(fromMenuItems(menu.items));
    setDirty(false);
    setErrors(new Map());
    setRootError(null);
  };

  useSaveBarEntry({
    dirty,
    saving: replace.isPending,
    onSave: save,
    onDiscard: discard,
    disabled: !canWrite,
  });

  const onSaveItem = (patch: MenuItemPatch) => {
    if (editing.mode === 'edit') change(updateNode(nodes, editing.path, patch));
    if (editing.mode === 'add') {
      change(appendNode(nodes, editing.parentPath, { ...editing.node, ...patch }));
    }
  };

  return (
    <FormSection
      title={t('tree.title')}
      description={t('tree.description')}
      tour="content.menu-editor.tree"
    >
      {rootError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}
      {nodes.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
          {t('tree.empty')}
        </p>
      ) : (
        <ol aria-label={t('tree.label')} className="flex flex-col gap-1.5">
          {nodes.map((node, index) => (
            <NodeRow
              key={node.key}
              node={node}
              path={[index]}
              siblings={nodes.length}
              nodes={nodes}
              canWrite={canWrite}
              errors={errors}
              onEdit={(path, target) => setEditing({ mode: 'edit', path, node: target })}
              onAddChild={(parentPath) =>
                setEditing({ mode: 'add', parentPath, node: emptyNode() })
              }
              onChange={change}
            />
          ))}
        </ol>
      )}
      {canWrite ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={total >= MAX_ITEMS}
            onClick={() => setEditing({ mode: 'add', parentPath: [], node: emptyNode() })}
            data-tour="content.menu-editor.add-item"
          >
            <Plus aria-hidden />
            {t('tree.addItem')}
          </Button>
          <div className="flex items-center gap-2" data-tour="content.menu-editor.save">
            <span className="text-muted-foreground text-xs">
              {t('tree.count', { count: total, max: MAX_ITEMS })}
            </span>
            <Button
              type="button"
              variant="ghost"
              disabled={!dirty || replace.isPending}
              onClick={discard}
            >
              {t('actions.discard')}
            </Button>
            <Button
              type="button"
              disabled={!dirty || replace.isPending}
              onClick={() => void save()}
            >
              {replace.isPending ? t('tree.saving') : t('tree.save')}
            </Button>
          </div>
        </div>
      ) : null}
      {editing.mode !== 'closed' ? (
        <MenuItemDialog
          key={editing.node.key}
          storeId={storeId}
          node={editing.node}
          isNew={editing.mode === 'add'}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ mode: 'closed' });
          }}
          onSave={onSaveItem}
        />
      ) : null}
    </FormSection>
  );
}
